import React, { useEffect, useState } from 'react';
import ReportLayout from '../../components/reports/ReportLayout';
import ProfitLossDetails from '../../components/reports/ProfitLossDetails';
import FinancialReportSection, {
  FinancialReportRow
} from '../../components/reports/FinancialReportSection';
import { profitLossService } from '../../services/profitLossService';
import { downloadProfitLossCsv, downloadProfitLossPdf } from '../../utils/profitLossExport';
import { useClient } from '../../context/ClientContext';

const getAccountExportRows = (reportData) => (
  [
    ['income', 'Income'],
    ['costOfGoodsSold', 'Cost of Goods Sold'],
    ['expenses', 'Operating Expenses']
  ].flatMap(([sectionKey, transactionType]) => (
    reportData.accountDetails[sectionKey].map((account) => ({
      category: account.name || '',
      amount: account.amount,
      transactionType
    }))
  ))
);

export default function ProfitLossReport() {
  const { activeClient } = useClient();
  const [dates, setDates] = useState({
    startDate: `${new Date().getFullYear()}-01-01`,
    endDate: new Date().toISOString().split('T')[0]
  });
  const [basis, setBasis] = useState('accrual');
  const [viewMode, setViewMode] = useState('summary');
  const [showZeroBalances, setShowZeroBalances] = useState(false);
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState(null);
  const [exportState, setExportState] = useState({ status: '', message: '' });
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [error, setError] = useState(null);

  const fetchReport = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await profitLossService.generate(
        dates.startDate,
        dates.endDate,
        activeClient?.id,
        basis
      );
      setReportData(data);
    } catch (err) {
      setError(err.message || 'Unable to load the report.');
      setReportData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, [dates.startDate, dates.endDate, basis, activeClient?.id]);

  const showExportMessage = (status, message) => {
    setExportState({ status, message });
    window.setTimeout(() => {
      setExportState((current) => (
        current.message === message ? { status: '', message: '' } : current
      ));
    }, 3500);
  };

  const handleExport = async (format) => {
    setShowExportMenu(false);

    if (!reportData) {
      showExportMessage('error', 'No records available to export.');
      return;
    }

    const rows = viewMode === 'details'
      ? getAccountExportRows(reportData)
      : [
        { category: 'Total Income', amount: reportData.income.total },
        { category: 'Total Cost of Goods Sold', amount: reportData.costOfGoodsSold.total },
        { category: 'Gross Profit', amount: reportData.grossProfit },
        { category: 'Total Operating Expenses', amount: reportData.expenses.total },
        { category: 'Net Profit (Loss)', amount: reportData.netIncome }
      ];

    showExportMessage('loading', `Generating ${format.toUpperCase()}...`);

    try {
      const options = {
        companyName: activeClient?.legal_name || activeClient?.name || 'Accounting Workspace',
        dates,
        basis,
        reportData,
        rows,
        viewMode,
        showZeroBalances,
        fileName: `Income_Expense_Report_${new Date().toISOString().slice(0, 10)}.${format}`
      };

      if (format === 'csv') {
        downloadProfitLossCsv(options);
      } else {
        downloadProfitLossPdf({
          ...options,
          logoUrl: activeClient?.logo_url || reportData.logoUrl
        });
      }

      showExportMessage('success', 'Export completed successfully.');
    } catch (exportError) {
      showExportMessage('error', exportError.message || 'Export failed.');
    }
  };

  if (error) {
    return (
      <div className="mx-auto max-w-5xl rounded-lg border border-rose-200 bg-rose-50 p-5 text-sm text-rose-700">
        {error}
        <button
          type="button"
          onClick={fetchReport}
          className="ml-3 font-medium underline"
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <ReportLayout
      title="Income & Expenses"
      description="Chart of Accounts based income statement."
      viewMode={viewMode}
      setViewMode={setViewMode}
      startDate={dates.startDate}
      endDate={dates.endDate}
      onStartDateChange={(value) => setDates((current) => ({ ...current, startDate: value }))}
      onEndDateChange={(value) => setDates((current) => ({ ...current, endDate: value }))}
      showBasis
      basis={basis}
      setBasis={setBasis}
      onRefresh={fetchReport}
      loading={loading}
      onExport={handleExport}
      exportOpen={showExportMenu}
      setExportOpen={setShowExportMenu}
    >
      {exportState.message && (
        <div className="mb-4 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
          {exportState.message}
        </div>
      )}

      {reportData && viewMode === 'summary' && (
        <div>
          <FinancialReportSection
            title="Income"
            totalLabel="Total Income"
            total={reportData.income.total}
            emptyLabel=""
          />
          <FinancialReportSection
            title="Cost of Goods Sold"
            totalLabel="Total Cost of Goods Sold"
            total={reportData.costOfGoodsSold.total}
            emptyLabel=""
          />
          <FinancialReportRow
            label="Gross Profit"
            value={reportData.grossProfit}
            total
            grand
          />
          <FinancialReportSection
            title="Operating Expenses"
            totalLabel="Total Operating Expenses"
            total={reportData.expenses.total}
            emptyLabel=""
          />
          <FinancialReportRow
            label="Net Profit (Loss)"
            value={reportData.netIncome}
            total
            grand
          />
        </div>
      )}

      {reportData && viewMode === 'details' && (
        <ProfitLossDetails
          reportData={reportData}
          showZeroBalances={showZeroBalances}
          onToggleZeroBalances={setShowZeroBalances}
        />
      )}

      {!loading && !reportData && (
        <p className="py-10 text-center text-sm text-slate-500">
          No report data is available.
        </p>
      )}
    </ReportLayout>
  );
}